// src/CompanyRegister/Step2.js
import React, { useState } from "react";
import companyData from "../Data/CompanyData";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const predefinedCertifications = [
  "ISO 9001 (Calidad)",
  "ISO 14001 (Medio Ambiente)",
  "ISO 45001 (Seguridad y Salud)",
  "ISO 22000 (Seguridad Alimentaria)",
  "BRC (British Retail Consortium)",
  "HACCP (Análisis de Peligros y Puntos Críticos de Control)",
];

const Step2 = ({ onNext, onBack, formData }) => {
  const [descripcion, setDescripcion] = useState(formData?.descripcion || "");
  const [numeroEmpleados, setNumeroEmpleados] = useState(
    formData?.numeroEmpleados || ""
  );
  const [certificaciones, setCertificaciones] = useState(
    formData?.certificaciones || []
  );
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customCertification, setCustomCertification] = useState("");

  const sectorData = companyData.sectores.find(
    (sector) => sector.label === formData.sector
  );
  // Usa la descripción del sector o un texto por defecto si no se encuentra
  const placeholderText = sectorData
    ? sectorData.description
    : "Describe brevemente tu empresa.";

  console.log(formData);

  const handleAddCertification = () => {
    if (!customCertification.trim()) {
      toast.warning("Por favor, ingresa una certificación válida.");
      return;
    }

    // Validar límite de 5 certificaciones personalizadas
    const customCertifications = certificaciones.filter(
      (cert) => !predefinedCertifications.includes(cert)
    );

    if (customCertifications.length >= 5) {
      toast.warning(
        "Solo puedes agregar un máximo de 5 certificaciones personalizadas."
      );
      return;
    }

    setCertificaciones((prev) => [...prev, customCertification.trim()]);
    setCustomCertification("");
    setShowCustomInput(false);
  };

  const handleToggleCertification = (cert) => {
    setCertificaciones(
      (prev) =>
        prev.includes(cert)
          ? prev.filter((c) => c !== cert) // Eliminar si ya está seleccionada
          : [...prev, cert] // Agregar si no está seleccionada
    );
  };

  const handleNext = () => {
    if (!descripcion) {
      toast.warning("Por favor completa la descripción.");
      return;
    }

    if (!numeroEmpleados) {
      toast.warning("Por favor selecciona el número de empleados.");
      return;
    }

    onNext({ descripcion, numeroEmpleados, certificaciones });
  };

  const handleBack = () => {
    onBack({ descripcion, numeroEmpleados, certificaciones }); // Envía los datos actuales al componente padre
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <h3 className="text-xl font-semibold text-foreground">Paso 2: Descripción y Detalles</h3>
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Descripción de la Empresa</label>
            <textarea
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder={placeholderText}
              maxLength={2000}
              rows={6}
            ></textarea>
            <small className="text-xs text-muted-foreground">{`${descripcion.length}/2000 caracteres`}</small>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Número de empleados</label>
            <select
              id="numeroEmpleados"
              value={numeroEmpleados}
              onChange={(e) => setNumeroEmpleados(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">Seleccione el número de empleados</option>
              <option value="1">1 empleado</option>
              <option value="2 - 10">2 - 10 empleados</option>
              <option value="11 - 50">11 - 50 empleados</option>
              <option value="51 - 200">51 - 200 empleados</option>
              <option value="201 - 1000">201 - 1000 empleados</option>
              <option value="1001+">Más de 1000 empleados</option>
            </select>
          </div>

          {/* Certificaciones */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Certificaciones (opcional)</label>
            <div className="flex flex-wrap gap-2">
              {predefinedCertifications.map((cert) => (
                <button
                  key={cert}
                  type="button"
                  onClick={() => handleToggleCertification(cert)}
                  className={cn(
                    "rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted",
                    certificaciones.includes(cert) && "border-primary bg-primary/10 text-primary"
                  )}
                >
                  {cert}
                </button>
              ))}
              {certificaciones
                .filter((cert) => !predefinedCertifications.includes(cert))
                .map((customCert) => (
                  <button
                    key={customCert}
                    type="button"
                    onClick={() =>
                      setCertificaciones((prev) =>
                        prev.filter((c) => c !== customCert)
                      )
                    }
                    className="rounded-md border border-primary bg-primary/10 px-3 py-1.5 text-sm text-primary"
                  >
                    {customCert}
                  </button>
                ))}
              <button
                type="button"
                className="rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
                onClick={() => setShowCustomInput(!showCustomInput)}
              >
                + Agregar Otra
              </button>
            </div>

            {showCustomInput && (
              <div className="mt-2 flex gap-2">
                <Input
                  type="text"
                  value={customCertification}
                  onChange={(e) => setCustomCertification(e.target.value)}
                  placeholder="Agrega una certificación distinta"
                  maxLength={100}
                />
                <Button type="button" variant="outline" onClick={handleAddCertification}>
                  OK
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={handleBack}>
              Retroceder
            </Button>
            <Button type="button" onClick={handleNext}>
              Siguiente
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default Step2;
